import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const STATUS_TONE = {
  open: "bg-blue-100 text-blue-700", waiting_on_staff: "bg-amber-100 text-amber-700",
  waiting_on_client: "bg-indigo-100 text-indigo-700", escalated: "bg-rose-100 text-rose-700",
  resolved: "bg-emerald-100 text-emerald-700", archived: "bg-slate-200 text-slate-500",
};
const CHANNEL_ICON = { portal: "Globe", penny: "Bot", email: "Mail", sms: "MessageSquare", whatsapp: "MessageCircle", line: "MessageCircle", phone_note: "Phone", meeting_note: "CalendarClock", system: "Settings", webchat: "Globe" };
const COMPOSER_CHANNELS = ["portal", "email", "sms", "whatsapp", "line", "phone_note"];
const PRIORITY_TONE = { high: "text-rose-600", urgent: "text-rose-700", normal: "text-slate-400", low: "text-slate-300" };
const VIEW_TITLES = { all: "Unified Inbox", mine: "My Conversations", team: "Team Conversations", unassigned: "Unassigned", client: "Client Messages", penny: "Penny Conversations", "internal-notes": "Internal Notes" };

function timeAgo(iso) {
  if (!iso) return "";
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "now"; if (d < 3600) return `${Math.floor(d / 60)}m`;
  if (d < 86400) return `${Math.floor(d / 3600)}h`; return `${Math.floor(d / 86400)}d`;
}

const SLA_TONE = { ok: "bg-emerald-100 text-emerald-700", warning: "bg-amber-100 text-amber-700", breached: "bg-rose-100 text-rose-700 animate-pulse" };
function fmtWait(mins) {
  if (mins == null) return "";
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  return `${Math.floor(mins / 1440)}d ${Math.floor((mins % 1440) / 60)}h`;
}
function SlaBadge({ state, mins, target, compact }) {
  if (!state || state === "answered") return null;
  const labels = { ok: "On time", warning: "Due soon", breached: "Overdue" };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full inline-flex items-center gap-0.5 ${SLA_TONE[state]}`} data-testid="sla-badge" title={target ? `Target ${fmtWait(target)}` : ""}>
      <Icons.Clock className="h-2.5 w-2.5" />{compact ? fmtWait(mins) : `${labels[state]} · ${fmtWait(mins)}`}
    </span>
  );
}

export default function UnifiedInbox({ view = "all" }) {
  const navigate = useNavigate();
  const { user: me } = useAuth();
  const [sp] = useSearchParams();
  const [items, setItems] = useState(null);
  const [counts, setCounts] = useState({});
  const [activeId, setActiveId] = useState(sp.get("c") || null);
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [reply, setReply] = useState("");
  const [noteMode, setNoteMode] = useState(false);
  const [channel, setChannel] = useState("portal");
  const [channelFilter, setChannelFilter] = useState(null);
  const isMgmt = ["administrator", "partner", "manager"].includes(me?.role);
  const [activeView, setActiveView] = useState(view);
  useEffect(() => { setActiveView(isMgmt ? view : "mine"); }, [view, isMgmt]);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null);
  const [users, setUsers] = useState([]);
  const threadRef = useRef(null);

  const loadList = useCallback(() => {
    const params = new URLSearchParams();
    if (statusFilter === "no_response") {
      params.set("inquiry", "visitor_no_response");
    } else {
      params.set("view", activeView);
      if (statusFilter !== "all") params.set("status", statusFilter);
    }
    if (q) params.set("q", q);
    if (channelFilter) { params.set("channel", channelFilter); params.set("unread_only", "true"); }
    api.get(`/communications/inbox?${params}`).then((r) => setItems(r.data.items)).catch(() => setItems([]));
    api.get("/communications/counts").then((r) => setCounts(r.data)).catch(() => {});
  }, [activeView, statusFilter, q, channelFilter]);
  useEffect(() => { const t = setTimeout(loadList, 200); return () => clearTimeout(t); }, [loadList]);

  const loadDetail = useCallback((id) => {
    if (!id) { setDetail(null); return; }
    api.get(`/communications/conversations/${id}`).then((r) => setDetail(r.data)).catch(() => toast.error("Could not load conversation"));
  }, []);
  useEffect(() => { loadDetail(activeId); }, [activeId, loadDetail]);
  useEffect(() => { if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight; }, [detail]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data.items || r.data || [])).catch(() => {}); }, []);

  const act = async (fn, msg) => {
    setBusy(true);
    try { await fn(); if (msg) toast.success(msg); loadList(); loadDetail(activeId); setModal(null); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };

  const send = async () => {
    if (!reply.trim() || !activeId) return;
    const body = reply; setReply("");
    try {
      if (noteMode) await api.post(`/communications/conversations/${activeId}/internal-note`, { body });
      else await api.post(`/communications/conversations/${activeId}/reply`, { body, channel });
      loadDetail(activeId); loadList();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); setReply(body); }
  };

  const [consulting, setConsulting] = useState(false);
  const consultAllison = async () => {
    if (!activeId || consulting) return;
    setConsulting(true);
    const t = toast.loading("Consulting Allison — analyzing this conversation…");
    try {
      const r = await api.post(`/communications/conversations/${activeId}/consult-allison`);
      toast.success(r.data?.source === "llm" ? "Allison added her tax analysis to the thread" : "Allison responded", { id: t });
      loadDetail(activeId);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not consult Allison", { id: t }); }
    finally { setConsulting(false); }
  };

  const conv = detail?.conversation;

  const assignToMe = async (e, cid) => {
    e.stopPropagation();
    try {
      await api.post(`/communications/conversations/${cid}/assign`, { assigned_user_id: me?.id });
      toast.success("Assigned to you — moved to your Mine tab");
      loadList();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not assign"); }
  };

  return (
    <div className="flex h-[calc(100vh-7rem)] -m-5 md:-m-8 bg-[#F6F7FB] overflow-hidden border border-slate-200 rounded-lg" data-testid="unified-inbox">
      {/* LEFT — queue list */}
      <div className="w-[340px] shrink-0 border-r border-slate-200 bg-white flex flex-col">
        <div className="p-4 border-b border-slate-100">
          <div className="flex items-center justify-between">
            <h1 className="text-lg font-bold text-slate-900">{VIEW_TITLES[activeView] || "Inbox"}</h1>
            <span className="text-xs text-amber-600 font-medium" data-testid="inbox-unanswered-count">{counts.unanswered || 0} waiting</span>
          </div>
          {isMgmt && (
            <div className="flex gap-1 mt-3" data-testid="inbox-view-tabs">
              {[["mine", "Mine", "mine"], ["all", "All", "all"], ["unassigned", "Unassigned", "unassigned"], ["penny", "Penny", "penny"]].map(([v, label, countKey]) => (
                <button key={v} onClick={() => setActiveView(v)} data-testid={`inbox-view-tab-${v}`}
                  className={`text-xs px-2.5 py-1.5 rounded-md flex items-center gap-1.5 transition-colors ${activeView === v ? "bg-blue-600 text-white" : "text-slate-500 hover:bg-slate-100"}`}>
                  {label}
                  {counts[countKey] > 0 && <span className={`min-w-[16px] h-4 px-1 rounded-full text-[10px] font-semibold flex items-center justify-center ${activeView === v ? "bg-white/25 text-white" : "bg-slate-200 text-slate-600"}`}>{counts[countKey]}</span>}
                </button>
              ))}
            </div>
          )}
          <div className="relative mt-3">
            <Icons.Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="inbox-search" placeholder="Search conversations…" className="w-full bg-slate-50 border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200" />
          </div>
          <div className="flex flex-wrap gap-1 mt-2">
            {["all", "open", "waiting_on_staff", "resolved", "no_response"].map((s) => (
              <button key={s} onClick={() => setStatusFilter(s)} data-testid={`inbox-filter-${s}`}
                className={`text-[11px] px-2 py-1 rounded-md flex items-center gap-1 ${statusFilter === s ? (s === "no_response" ? "bg-slate-500 text-white" : "bg-slate-900 text-white") : "text-slate-500 hover:bg-slate-100"}`}>
                {s === "all" ? "All" : s === "no_response" ? "No Response" : s.replace(/_/g, " ")}
                {s === "no_response" && counts.no_response > 0 && (
                  <span className={`min-w-[15px] h-4 px-1 rounded-full text-[9px] font-semibold flex items-center justify-center ${statusFilter === s ? "bg-white/25 text-white" : "bg-slate-200 text-slate-600"}`}>{counts.no_response}</span>
                )}
              </button>
            ))}
          </div>
          {channelFilter && (
            <div className="mt-2 flex items-center gap-1.5 text-[11px] bg-rose-50 text-rose-700 border border-rose-200 rounded-md px-2 py-1 w-fit" data-testid="inbox-channel-filter-chip">
              <Icons.Filter className="h-3 w-3" />
              <span className="capitalize">Unanswered · {channelFilter.replace(/_/g, " ")}</span>
              <button onClick={() => setChannelFilter(null)} data-testid="inbox-channel-filter-clear" className="ml-0.5 hover:text-rose-900"><Icons.X className="h-3 w-3" /></button>
            </div>
          )}
        </div>
        {statusFilter === "no_response" && (
          <div className="px-4 py-2 text-[11px] text-slate-500 bg-slate-50 border-b border-slate-100 flex items-center gap-1.5" data-testid="inbox-no-response-note">
            <Icons.Ghost className="h-3.5 w-3.5 text-slate-400" />
            Visitors who opened the chat but never replied. Auto-archived from alerts &amp; purged after 30 days.
          </div>
        )}
        <div className="flex-1 overflow-y-auto">
          {items === null ? <div className="py-12 text-center"><Icons.Loader2 className="h-5 w-5 animate-spin text-blue-500 inline" /></div> :
          items.length === 0 ? <div className="py-12 text-center text-slate-400 text-sm">No conversations.</div> :
          items.map((c, ci) => {
            const Ch = Icons[CHANNEL_ICON[c.channel] || "MessageSquare"];
            return (
              <button key={`${c.id}-${ci}`} onClick={() => { setActiveId(c.id); navigate(`?c=${c.id}`, { replace: true }); }} data-testid={`inbox-row-${c.id}`}
                className={`w-full text-left px-4 py-3 border-b border-slate-50 hover:bg-slate-50 ${activeId === c.id ? "bg-blue-50/60 border-l-2 border-l-blue-500" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-slate-800 text-sm truncate">{c.client_name || "—"}</span>
                  <span className="text-[11px] text-slate-400 shrink-0">{timeAgo(c.last_message_at)}</span>
                </div>
                <div className="text-xs text-slate-500 truncate mt-0.5">{c.last_message || c.subject || "No messages"}</div>
                <div className="flex items-center gap-1.5 mt-1.5">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${STATUS_TONE[c.conv_status] || "bg-slate-100"}`}>{(c.conv_status || "open").replace(/_/g, " ")}</span>
                  <Ch className="h-3 w-3 text-slate-400" />
                  {c.priority === "high" && <Icons.Flame className="h-3 w-3 text-rose-500" />}
                  <SlaBadge state={c.sla_state} mins={c.waiting_minutes} target={c.sla_target_minutes} compact />
                  {c.unread_count > 0 && <span className="ml-auto text-[10px] bg-rose-500 text-white rounded-full px-1.5 py-0.5" data-testid={`inbox-unread-${c.id}`}>{c.unread_count}</span>}
                </div>
                {activeView === "unassigned" && (
                  <span role="button" tabIndex={0} onClick={(e) => assignToMe(e, c.id)} data-testid={`inbox-assign-me-${c.id}`}
                    className="mt-2 inline-flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-white hover:bg-blue-600 border border-blue-200 rounded-md px-2 py-1 transition-colors cursor-pointer">
                    <Icons.UserPlus className="h-3 w-3" />Assign to me
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* CENTER — thread */}
      <div className="flex-1 flex flex-col min-w-0">
        {!conv ? (
          <div className="flex-1 flex items-center justify-center text-slate-400"><div className="text-center"><Icons.MessagesSquare className="h-10 w-10 mx-auto mb-2 text-slate-300" />Select a conversation</div></div>
        ) : (
          <>
            <div className="px-5 py-3 border-b border-slate-200 bg-white flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h2 className="font-semibold text-slate-900 truncate">{conv.subject || detail.client?.full_name}</h2>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full ${STATUS_TONE[conv.conv_status] || "bg-slate-100"}`} data-testid="conv-status">{(conv.conv_status || "open").replace(/_/g, " ")}</span>
                  <SlaBadge state={conv.sla_state} mins={conv.waiting_minutes} target={conv.sla_target_minutes} />
                </div>
                <div className="text-xs text-slate-400">{detail.client?.full_name} · {detail.client?.client_number} · {conv.channel}</div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <button onClick={consultAllison} disabled={consulting} data-testid="conv-consult-allison-btn" title="Consult Allison — pull her tax analysis into this thread" className="text-sm rounded-lg px-3 py-1.5 flex items-center gap-1.5 font-medium text-white bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-500 hover:to-violet-500 disabled:opacity-60 transition-colors">
                  {consulting ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.Sparkles className="h-4 w-4" />}Consult Allison
                </button>
                <button onClick={() => setModal("assign")} data-testid="conv-assign-btn" title="Assign" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><Icons.UserPlus className="h-4 w-4" /></button>
                <button onClick={() => setModal("escalate")} data-testid="conv-escalate-btn" title="Escalate" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><Icons.AlertTriangle className="h-4 w-4" /></button>
                <button onClick={() => setModal("task")} data-testid="conv-task-btn" title="Create task" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><Icons.CheckSquare className="h-4 w-4" /></button>
                <button onClick={() => setModal("ir")} data-testid="conv-ir-btn" title="Information request" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><Icons.HelpCircle className="h-4 w-4" /></button>
                <button onClick={() => act(() => api.post(`/communications/conversations/${activeId}/status`, { status: "resolved" }), "Resolved")} data-testid="conv-resolve-btn" className="text-sm border border-emerald-200 text-emerald-700 rounded-lg px-3 py-1.5 hover:bg-emerald-50 flex items-center gap-1"><Icons.CheckCircle2 className="h-4 w-4" />Resolve</button>
                <button onClick={() => act(() => api.post(`/communications/conversations/${activeId}/archive`), "Archived")} data-testid="conv-archive-btn" title="Archive" className="p-2 rounded-lg hover:bg-slate-100 text-slate-500"><Icons.Archive className="h-4 w-4" /></button>
              </div>
            </div>

            <div ref={threadRef} className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
              {detail.messages.map((m, mi) => {
                const isInternal = m.internal_note || m.direction === "internal";
                const isOut = m.direction === "outbound" || m.sender_type === "staff" || m.sender_type === "penny";
                if (isInternal) {
                  const isAllison = m.sender_type === "allison";
                  return (
                    <div key={`${m.id}-${mi}`} className="flex justify-center" data-testid={isAllison ? `msg-allison-${m.id}` : `msg-internal-${m.id}`}>
                      <div className={`max-w-[85%] rounded-lg px-3.5 py-2 text-sm whitespace-pre-wrap ${isAllison ? "bg-indigo-50 border border-indigo-200 text-indigo-900" : "bg-amber-50 border border-amber-200 text-amber-800"}`}>
                        <div className={`flex items-center gap-1 text-[11px] mb-1 ${isAllison ? "text-indigo-600" : "text-amber-600"}`}>
                          {isAllison ? <Icons.Sparkles className="h-3 w-3" /> : <Icons.Lock className="h-3 w-3" />}
                          {isAllison ? "Tax Intelligence · internal" : `Internal note · ${m.sender_name}`}
                        </div>
                        {m.body}
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={`${m.id}-${mi}`} className={`flex ${isOut ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                    <div className={`max-w-[72%] rounded-2xl px-4 py-2.5 text-sm ${isOut ? "bg-[#1f6feb] text-white" : "bg-white border border-slate-200 text-slate-700"}`}>
                      {!isOut && <div className="text-[11px] text-slate-400 mb-0.5">{m.sender_name}{m.sender_type === "penny" ? " · Penny" : ""}</div>}
                      <div className="whitespace-pre-wrap">{m.body}</div>
                      <div className={`text-[10px] mt-1 ${isOut ? "text-blue-100" : "text-slate-400"}`}>{timeAgo(m.sent_at)} {m.delivered_status === "placeholder_unsent" ? "· placeholder" : ""}</div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-slate-200 bg-white px-4 py-3">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <button onClick={() => setNoteMode(false)} data-testid="composer-reply-tab" className={`text-xs px-2.5 py-1 rounded-md ${!noteMode ? "bg-blue-600 text-white" : "text-slate-500 hover:bg-slate-100"}`}>Reply</button>
                <button onClick={() => setNoteMode(true)} data-testid="composer-note-tab" className={`text-xs px-2.5 py-1 rounded-md flex items-center gap-1 ${noteMode ? "bg-amber-500 text-white" : "text-slate-500 hover:bg-slate-100"}`}><Icons.Lock className="h-3 w-3" />Internal note</button>
                {!noteMode && (
                  <div className="flex flex-row-reverse flex-wrap items-center gap-1.5 ml-auto" data-testid="composer-channels">
                    {COMPOSER_CHANNELS.map((c) => {
                      const Ch = Icons[CHANNEL_ICON[c] || "MessageSquare"];
                      const cnt = counts.unanswered_by_channel?.[c] || 0;
                      const active = channel === c;
                      return (
                        <button key={c} onClick={() => setChannel(c)} data-testid={`composer-channel-${c}`} title={`Reply via ${c.replace(/_/g, " ")}${cnt ? ` · ${cnt} unanswered` : ""}`}
                          className={`relative inline-flex items-center gap-1 text-[11px] px-2.5 py-1 rounded-full border transition-colors ${active ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-200 text-slate-600 hover:border-blue-300 hover:text-blue-600"}`}>
                          <Ch className="h-3.5 w-3.5" />
                          <span className="capitalize">{c.replace(/_/g, " ")}</span>
                          {cnt > 0 && (
                            <span role="button" tabIndex={0} onClick={(e) => { e.stopPropagation(); setChannelFilter(c); }} data-testid={`composer-channel-badge-${c}`} title={`Show ${cnt} unanswered ${c.replace(/_/g, " ")} conversation${cnt > 1 ? "s" : ""}`} className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 hover:bg-rose-600 text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white cursor-pointer">{cnt > 99 ? "99+" : cnt}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="flex items-end gap-2">
                <textarea value={reply} onChange={(e) => setReply(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  data-testid="composer-input" rows={2} placeholder={noteMode ? "Add a private internal note (use @name to mention)…" : "Type your message…"}
                  className={`flex-1 border rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 ${noteMode ? "bg-amber-50/40 border-amber-200 focus:ring-amber-200" : "border-slate-200 focus:ring-blue-200"}`} />
                <button onClick={send} disabled={!reply.trim()} data-testid="composer-send" className="bg-[#1f6feb] hover:bg-[#1a5fd0] disabled:opacity-50 text-white rounded-lg p-2.5"><Icons.Send className="h-4 w-4" /></button>
              </div>
              {!noteMode && channel !== "portal" && <p className="text-[11px] text-amber-600 mt-1.5">⚠ {channel.toUpperCase()} provider not configured — message will be saved as a placeholder.</p>}
            </div>
          </>
        )}
      </div>

      {/* RIGHT — context */}
      {conv && (
        <div className="w-[300px] shrink-0 border-l border-slate-200 bg-white overflow-y-auto p-4 space-y-4" data-testid="conv-context">
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wide mb-1">Client</div>
            <div className="font-semibold text-slate-800">{detail.client?.full_name}</div>
            <div className="text-xs text-slate-500">{detail.client?.email}</div>
            <div className="text-xs text-slate-500">{detail.client?.phone}</div>
            <button onClick={() => navigate(`/client-workspace/${conv.client_id}`)} data-testid="conv-open-workspace" className="mt-2 text-xs text-blue-600 hover:underline flex items-center gap-1"><Icons.ExternalLink className="h-3.5 w-3.5" />Open Client Workspace</button>
          </div>
          <div className="border-t border-slate-100 pt-3 space-y-1.5 text-sm">
            <div className="flex justify-between"><span className="text-slate-400">Assigned</span><span className="text-slate-700">{conv.assigned_user_name || "Unassigned"}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Priority</span><span className={`capitalize ${PRIORITY_TONE[conv.priority] || "text-slate-500"}`}>{conv.priority}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Type</span><span className="text-slate-700 capitalize text-right">{(conv.conversation_type || "").replace(/_/g, " ")}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Sentiment</span><span className="text-slate-700 capitalize">{conv.sentiment}</span></div>
          </div>
          {detail.engagement && <div className="border-t border-slate-100 pt-3"><div className="text-xs text-slate-400 uppercase tracking-wide mb-1">Engagement</div><div className="text-sm font-mono text-slate-700">{detail.engagement.engagement_number}</div></div>}
          {detail.information_requests?.length > 0 && (
            <div className="border-t border-slate-100 pt-3"><div className="text-xs text-slate-400 uppercase tracking-wide mb-1">Open Info Requests</div>
              {detail.information_requests.map((ir) => <div key={ir.id} className="text-xs text-slate-600 py-0.5">• {ir.title}</div>)}</div>
          )}
          {detail.tasks?.length > 0 && (
            <div className="border-t border-slate-100 pt-3"><div className="text-xs text-slate-400 uppercase tracking-wide mb-1">Open Tasks</div>
              {detail.tasks.map((t) => <div key={t.id} className="text-xs text-slate-600 py-0.5">• {t.title}</div>)}</div>
          )}
          <div className="border-t border-slate-100 pt-3 bg-slate-50 -mx-4 px-4 py-3">
            <div className="text-xs text-slate-400 uppercase tracking-wide mb-1 flex items-center gap-1"><Icons.Bot className="h-3.5 w-3.5" />Allison Suggestions</div>
            {detail.penny?.suggestions?.map((s, i) => (
              <button key={i} onClick={() => setReply(s)} data-testid={`allison-suggestion-${i}`} className="block w-full text-left text-xs text-slate-600 hover:text-blue-600 py-1 border-b border-slate-100 last:border-0">{s}</button>
            ))}
            <p className="text-[10px] text-slate-400 mt-1.5 italic">{detail.penny?.note}</p>
          </div>
        </div>
      )}

      {modal === "assign" && <AssignModal users={users} onClose={() => setModal(null)} onSave={(b) => act(() => api.post(`/communications/conversations/${activeId}/assign`, b), "Assigned")} />}
      {modal === "escalate" && <EscalateModal onClose={() => setModal(null)} onSave={(b) => act(() => api.post(`/communications/conversations/${activeId}/escalate`, b), "Escalated")} />}
      {modal === "task" && <TaskModal onClose={() => setModal(null)} onSave={(b) => act(() => api.post(`/communications/conversations/${activeId}/task`, b), "Task created")} />}
      {modal === "ir" && <IRModal onClose={() => setModal(null)} onSave={(b) => act(() => api.post(`/communications/conversations/${activeId}/information-request`, b), "Information request created")} />}
    </div>
  );
}

const ModalShell = ({ title, onClose, children }) => (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
    <div className="bg-white rounded-2xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between"><h3 className="font-semibold text-slate-900">{title}</h3><button onClick={onClose}><Icons.X className="h-5 w-5 text-slate-400" /></button></div>
      <div className="p-5">{children}</div>
    </div>
  </div>
);

function AssignModal({ users, onClose, onSave }) {
  const [uid, setUid] = useState("");
  return <ModalShell title="Assign Conversation" onClose={onClose}>
    <select value={uid} onChange={(e) => setUid(e.target.value)} data-testid="assign-user-select" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-4">
      <option value="">Select a teammate…</option>
      {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
    </select>
    <button onClick={() => onSave({ assigned_user_id: uid })} disabled={!uid} data-testid="assign-submit" className="w-full bg-[#1f6feb] text-white rounded-lg py-2.5 font-medium disabled:opacity-50">Assign</button>
  </ModalShell>;
}

function EscalateModal({ onClose, onSave }) {
  const [target, setTarget] = useState("case_manager"); const [note, setNote] = useState("");
  const targets = ["case_manager", "team_lead", "partner", "finance", "production", "client_success"];
  return <ModalShell title="Escalate Conversation" onClose={onClose}>
    <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="escalate-target" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-3 capitalize">
      {targets.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}
    </select>
    <textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="escalate-note" rows={3} placeholder="Reason (optional)" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-4" />
    <button onClick={() => onSave({ target, note })} data-testid="escalate-submit" className="w-full bg-rose-600 text-white rounded-lg py-2.5 font-medium">Escalate</button>
  </ModalShell>;
}

function TaskModal({ onClose, onSave }) {
  const [title, setTitle] = useState(""); const [priority, setPriority] = useState("normal");
  return <ModalShell title="Create Task" onClose={onClose}>
    <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="task-title" placeholder="Task title" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-3" />
    <select value={priority} onChange={(e) => setPriority(e.target.value)} data-testid="task-priority" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-4"><option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option></select>
    <button onClick={() => onSave({ title, priority })} disabled={!title} data-testid="task-submit" className="w-full bg-[#1f6feb] text-white rounded-lg py-2.5 font-medium disabled:opacity-50">Create Task</button>
  </ModalShell>;
}

function IRModal({ onClose, onSave }) {
  const [title, setTitle] = useState(""); const [description, setDescription] = useState("");
  return <ModalShell title="Create Information Request" onClose={onClose}>
    <input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="ir-title" placeholder="What do you need?" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-3" />
    <textarea value={description} onChange={(e) => setDescription(e.target.value)} data-testid="ir-description" rows={3} placeholder="Details (optional)" className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm mb-4" />
    <button onClick={() => onSave({ title, description })} disabled={!title} data-testid="ir-submit" className="w-full bg-[#1f6feb] text-white rounded-lg py-2.5 font-medium disabled:opacity-50">Send to Client</button>
  </ModalShell>;
}
